import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/account/Navbar";
import ActiveUrgentCasesBanner from "../../components/urgent/ActiveUrgentCasesBanner";
import "../../styles/guardiandash.css";
import { petAPI, urgentCaseAPI } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";

const UrgentCaseSubmit = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  // Replace this with your actual user fetching logic
  const DESCRIPTION_LIMIT = 500;

  const [pets, setPets] = useState([]);
  const { userId } = useParams();
  const { isAuthenticated, getUserType } = useAuth();
  const userType = getUserType();
  const [selectedPet, setSelectedPet] = useState(null);
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [ignoreLocation, setIgnoreLocation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pinPosition, setPinPosition] = useState({ x: 50, y: 50 }); // Percentage position
  const [mapCenter, setMapCenter] = useState({ lat: 31.9539, lng: 35.9106, zoom: 13 }); // Default to Amman, Jordan
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showNoPetsModal, setShowNoPetsModal] = useState(false);
  const [isLoadingPets, setIsLoadingPets] = useState(true);
  const navigate = useNavigate();

  const handleCancel = () => {
    const dashboardPath = userType === 'SERVICE_PROVIDER'
      ? '/medical/dashboard/petshopowner'
      : '/medical/dashboard/petguardian';
    navigate(`${dashboardPath}?tab=sos`);
  };

  const handlePetSelect = (id) => setSelectedPet(id);

  // Helper function to format validation errors from API response
  const formatValidationErrors = (error) => {
    // Check if there are validation errors in the response
    if (error?.response?.data?.errors) {
      const errors = error.response.data.errors;
      const errorMessages = [];

      // Iterate through all error fields
      Object.keys(errors).forEach((field) => {
        const fieldErrors = errors[field];
        // Handle both array and single string errors
        if (Array.isArray(fieldErrors)) {
          fieldErrors.forEach((msg) => errorMessages.push(msg));
        } else if (typeof fieldErrors === 'string') {
          errorMessages.push(fieldErrors);
        }
      });

      // Return formatted error messages
      if (errorMessages.length > 0) {
        return errorMessages.join('. ');
      }
    }

    // Fallback to main message or default
    return error?.response?.data?.message || t("urgentCase.errors.submitFailed");
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = selectedFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      type: file.type.startsWith('image/') ? 'image' : 'video',
      url: URL.createObjectURL(file)
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleSubmit = () => {
    if (!selectedPet) {
      setError(t("urgentCase.errors.petRequired"));
      return;
    }
    if (!description.trim()) {
      setError(t("urgentCase.errors.descriptionRequired"));
      return;
    }

    // if (files.length === 0) {
    //   setError("Please upload at least one media file");
    //   return;
    // }

    setError("");

    // If ignore location restrictions is checked, submit directly without map modal
    if (ignoreLocation) {
      handleDirectSubmit();
    } else {
      setShowMapModal(true);
    }
  };

  const handleDirectSubmit = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const formData = new FormData();

      // Add pet_ids array
      formData.append('pet_ids[]', selectedPet);

      // Add problem description
      formData.append('problem_description', description);

      // Add ignore location restrictions flag
      formData.append('ignore_location_restrictions', ignoreLocation ? 1 : 0);

      // Add media files
      files.forEach((media, index) => {
        formData.append('media[]', media.file);
      });

      const response = await urgentCaseAPI.submitUrgentCase(formData);

      if (response.data.data.case.id) {
        // Store the case ID or any relevant data
        localStorage.setItem("urgentDescription", description);
        localStorage.setItem("urgentCaseId", response.data.data.case.id);
        // Navigate to the next page
        navigate(`/medical/emergency-broadcast/${response.data.data.case.id}`);
      } else {
        setError(response.data.message || t("urgentCase.errors.submitFailed"));
      }
    } catch (error) {
      console.error("Error submitting urgent case:", error);
      setError(formatValidationErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleConfirmLocation = async () => {
    if (!selectedLocation) {
      setError(t("urgentCase.errors.locationRequired"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const formData = new FormData();

      // Add pet_ids array
      formData.append('pet_ids[]', selectedPet);

      // Add problem description
      formData.append('problem_description', description);

      // Add location data
      formData.append('latitude', selectedLocation.lat);
      formData.append('longitude', selectedLocation.lng);

      // Add ignore location restrictions flag
      formData.append('ignore_location_restrictions', ignoreLocation ? 1 : 0);

      // Add media files
      files.forEach((media, index) => {
        formData.append('media[]', media.file);
      });

      const response = await urgentCaseAPI.submitUrgentCase(formData);

      if (response.data.data.case.id) {
        // Store the case ID or any relevant data
        localStorage.setItem("urgentDescription", description);
        localStorage.setItem("urgentCaseId", response.data.data.case.id);
        // Close modal and navigate to the next page
        setShowMapModal(false);
        navigate(`/medical/emergency-broadcast/${response.data.data.case.id}`);
      } else {
        setError(response.data.message || t("urgentCase.errors.submitFailed"));
      }
    } catch (error) {
      console.error("Error submitting urgent case:", error);
      setError(formatValidationErrors(error));
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseMapModal = () => {
    setShowMapModal(false);
    setSelectedLocation(null);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setSelectedLocation({ lat: latitude, lng: longitude });

          // Update the interactive map if it exists
          if (window.urgentCaseMap) {
            const newCenter = { lat: latitude, lng: longitude };
            window.urgentCaseMap.setCenter(newCenter);
            window.urgentCaseMap.setZoom(15);

            // Remove existing marker if it exists
            if (window.urgentCaseMapMarker) {
              window.urgentCaseMapMarker.setMap(null);
            }

            // Create new marker at current location
            const marker = new window.google.maps.Marker({
              position: newCenter,
              map: window.urgentCaseMap,
              title: t("urgentCase.location.map.currentLocation"),
              draggable: true,
              animation: window.google.maps.Animation.DROP,
            });

            // Add drag listener to the new marker
            marker.addListener("dragend", (event) => {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();

              setSelectedLocation({ lat: lat, lng: lng });
            });

            // Store marker reference globally
            window.urgentCaseMapMarker = marker;
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          setError(t("urgentCase.errors.locationFetch"));
        }
      );
    } else {
      setError(t("urgentCase.errors.geolocationUnsupported"));
    }
  };

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) {
      setError(t("urgentCase.errors.searchEmpty"));
      return;
    }

    setIsSearching(true);
    setError("");

    try {
      // Use Google Geocoding API to get coordinates
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          searchQuery
        )}&key=${import.meta.env.VITE_GOOGLE_MAP_API_KEY ||
        "AIzaSyBFw0Qbyq9zTFTd-tUY6dOWWgUfXrH3g1E"
        }`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        setSelectedLocation({ lat: location.lat, lng: location.lng });
        setSearchQuery(""); // Clear search input after successful search

        // Update the interactive map if it exists
        if (window.urgentCaseMap) {
          const newCenter = { lat: location.lat, lng: location.lng };
          window.urgentCaseMap.setCenter(newCenter);
          window.urgentCaseMap.setZoom(15);

          // Remove existing marker if it exists
          if (window.urgentCaseMapMarker) {
            window.urgentCaseMapMarker.setMap(null);
          }

          // Create new marker at searched location
          const marker = new window.google.maps.Marker({
            position: newCenter,
            map: window.urgentCaseMap,
            title: t("urgentCase.location.map.emergencyLocation"),
            draggable: true,
            animation: window.google.maps.Animation.DROP,
          });

          // Add drag listener to the new marker
          marker.addListener("dragend", (event) => {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            setSelectedLocation({ lat: lat, lng: lng });
          });

          // Store marker reference globally
          window.urgentCaseMapMarker = marker;
        }
      } else {
        setError(t("urgentCase.errors.locationNotFound"));
      }
    } catch (error) {
      console.error("Error searching location:", error);
      setError(t("urgentCase.errors.locationSearchFailed"));
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchLocation();
    }
  };

  // Clear pin functionality
  const handleClearPin = () => {
    setSelectedLocation(null);

    // Clear marker from map if it exists
    if (window.urgentCaseMap && window.urgentCaseMapMarker) {
      window.urgentCaseMapMarker.setMap(null);
      window.urgentCaseMapMarker = null;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    setIsLoadingPets(true);
    petAPI
      .getMyPets()
      .then((response) => {
        // Adjust if response structure is different
        const petsData = response.data?.data || response.data || [];
        setPets(petsData);

        // Check if user has no pets
        if (petsData.length === 0) {
          setShowNoPetsModal(true);
        }
      })
      .catch((error) => {
        setPets([]);
        // Optionally handle error
        console.error("Error fetching pets:", error);
        // Show modal even on error if no pets
        setShowNoPetsModal(true);
      })
      .finally(() => {
        setIsLoadingPets(false);
      });
  }, [isAuthenticated]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      files.forEach(file => {
        URL.revokeObjectURL(file.url);
      });
    };
  }, [files]);

  // Load Google Maps API and initialize interactive map
  useEffect(() => {
    if (!showMapModal) return; // Only load when modal is open

    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeInteractiveMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAP_API_KEY ||
        "AIzaSyBFw0Qbyq9zTFTd-tUY6dOWWgUfXrH3g1E"
        }&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeInteractiveMap();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API");
      };
      document.head.appendChild(script);
    };

    const initializeInteractiveMap = () => {
      const mapElement = document.getElementById("urgent-case-interactive-map");
      if (!mapElement || !window.google) return;

      // Default center (Amman, Jordan)
      const defaultCenter = { lat: 31.9539, lng: 35.9106 };

      // Use existing coordinates or default
      const center = selectedLocation || defaultCenter;

      const map = new window.google.maps.Map(mapElement, {
        zoom: 15,
        center: center,
        mapTypeId: "roadmap",
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      // Store map reference globally
      window.urgentCaseMap = map;

      // Function to create/update marker
      const createMarker = (position) => {
        // Remove existing marker
        if (window.urgentCaseMapMarker) {
          window.urgentCaseMapMarker.setMap(null);
        }

        // Create new marker
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: t("urgentCase.location.map.emergencyLocation"),
          draggable: true,
          animation: window.google.maps.Animation.DROP,
        });

        // Add drag listener
        marker.addListener("dragend", (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          setSelectedLocation({ lat: lat, lng: lng });
        });

        // Store marker reference globally
        window.urgentCaseMapMarker = marker;
        return marker;
      };

      // Add initial marker if coordinates exist
      if (selectedLocation) {
        createMarker(selectedLocation);
      }

      // Add click listener to place marker
      map.addListener("click", (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // Update selected location
        setSelectedLocation({ lat: lat, lng: lng });

        // Create marker at clicked position
        createMarker(event.latLng);
      });
    };

    // Load maps after a short delay to ensure DOM is ready
    const timer = setTimeout(loadGoogleMaps, 100);

    return () => {
      clearTimeout(timer);
      if (window.urgentCaseMap) {
        window.urgentCaseMap = null;
      }
      if (window.urgentCaseMapMarker) {
        window.urgentCaseMapMarker = null;
      }
    };
  }, [showMapModal, selectedLocation, t]);

  return (
    <div dir={i18n.dir()} lang={i18n.language} className={isRTL ? "urgentcase-root rtl" : "urgentcase-root"}>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="guardian-dash-root">
        {!showNoPetsModal && (
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Active Urgent Cases Banner */}
            <ActiveUrgentCasesBanner />

            <div style={{
              textAlign: 'center',
              marginBottom: '10px',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                backgroundColor: '#FEF2F2',
                borderRadius: '50%',
                marginBottom: '16px',
                border: '3px solid #FECACA',
                boxShadow: '0 8px 20px -5px rgba(239, 68, 68, 0.1), 0 4px 6px -2px rgba(239, 68, 68, 0.05)'
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 8px 0',
                fontFamily: '"Red Hat Display", sans-serif',
                letterSpacing: '-0.025em'
              }}>
                {t("urgentCase.hero.title")}
              </h2>
              <p style={{
                fontSize: '16px',
                color: '#6B7280',
                margin: '0',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: '1.5',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("urgentCase.hero.subtitle")}
              </p>
            </div>
            <div style={{
              marginBottom: '10px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #F3F4F6'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {t("urgentCase.pets.heading")}
              </h3>

              {isLoadingPets ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 20px',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #F3F4F6',
                    borderTop: '4px solid #10B981',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#6B7280',
                    fontFamily: '"Red Hat Display", sans-serif',
                    textAlign: 'center'
                  }}>
                    {t("urgentCase.pets.loading.message")}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#9CA3AF',
                    fontFamily: '"Red Hat Display", sans-serif',
                    textAlign: 'center'
                  }}>
                    {t("urgentCase.pets.loading.detail")}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '16px'
                }}>
                  {pets.map((pet) => (
                    <div
                      key={pet.id}
                      onClick={() => handlePetSelect(pet.id)}
                      style={{
                        backgroundColor: selectedPet === pet.id ? '#F0FDF4' : '#FFFFFF',
                        border: selectedPet === pet.id ? '2px solid #10B981' : '2px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        boxShadow: selectedPet === pet.id
                          ? '0 8px 20px -5px rgba(16, 185, 129, 0.1), 0 4px 6px -2px rgba(16, 185, 129, 0.05)'
                          : '0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
                        transform: selectedPet === pet.id ? 'translateY(-1px)' : 'translateY(0)'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPet !== pet.id) {
                          e.target.style.borderColor = '#D1D5DB'
                          e.target.style.transform = 'translateY(-1px)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPet !== pet.id) {
                          e.target.style.borderColor = '#E5E7EB'
                          e.target.style.transform = 'translateY(0)'
                        }
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          backgroundColor: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #E5E7EB'
                        }}>
                          <img
                            src={pet.profile_image_path || "/images/no-image-pet.png"}
                            alt={pet.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/images/no-image-pet.png";
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1F2937',
                            margin: '0 0 4px 0',
                            fontFamily: '"Red Hat Display", sans-serif'
                          }}>
                            {pet.name}
                          </h4>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                          }}>
                            <span style={{
                              fontSize: '14px',
                              color: '#6B7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                              </svg>
                              {typeof pet.species === 'object' ? (pet.species?.name || pet.species?.id || t("urgentCase.pets.unknown")) : (pet.species || t("urgentCase.pets.unknown"))}
                            </span>
                            <span style={{
                              fontSize: '14px',
                              color: '#6B7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12,6 12,12 16,14" />
                              </svg>
                              {pet.age_formatted || t("urgentCase.pets.unknownAge")}
                            </span>
                            {pet.breed && (
                              <span style={{
                                fontSize: '14px',
                                color: '#6B7280'
                              }}>
                                {typeof pet.breed === 'object' ? (pet.breed?.name || pet.breed?.id || t("urgentCase.pets.unknown")) : (pet.breed || t("urgentCase.pets.unknown"))}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedPet === pet.id && (
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#10B981',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            ✓
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{
              marginBottom: '10px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #F3F4F6'
            }}>
              <label style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
                {t("urgentCase.description.label")}
              </label>
              <div style={{ position: 'relative' }}>
                <textarea
                  placeholder={t("urgentCase.description.placeholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={DESCRIPTION_LIMIT}
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    color: '#1F2937',
                    backgroundColor: '#FFFFFF',
                    resize: 'vertical',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3B82F6'
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB'
                    e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  fontSize: '12px',
                  color: '#9CA3AF',
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  {t("urgentCase.description.counter", { count: description.length, max: DESCRIPTION_LIMIT })}
                </div>
              </div>
            </div>
            <div style={{
              marginBottom: '10px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #F3F4F6'
            }}>
              <label style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21,15 16,10 5,21" />
                </svg>
                {t("urgentCase.media.label")}
              </label>
              <div style={{
                border: '2px dashed #D1D5DB',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#FAFAFA',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative'
              }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.target.style.borderColor = '#8B5CF6'
                  e.target.style.backgroundColor = '#F3F4F6'
                }}
                onDragLeave={(e) => {
                  e.target.style.borderColor = '#D1D5DB'
                  e.target.style.backgroundColor = '#FAFAFA'
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.target.style.borderColor = '#D1D5DB'
                  e.target.style.backgroundColor = '#FAFAFA'
                  const files = Array.from(e.dataTransfer.files)
                  if (files.length > 0) {
                    const newFiles = files.map(file => ({
                      file,
                      id: Date.now() + Math.random(),
                      type: file.type.startsWith('image/') ? 'image' : 'video',
                      url: URL.createObjectURL(file)
                    }))
                    setFiles(prev => [...prev, ...newFiles])
                  }
                }}
                onClick={() => document.getElementById("urgentcase-file").click()}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #E5E7EB'
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1F2937',
                      margin: '0 0 6px 0',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                      {t("urgentCase.media.title")}
                    </h4>
                    <p style={{
                      fontSize: '13px',
                      color: '#6B7280',
                      margin: '0 0 12px 0',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                      {t("urgentCase.media.subtitle")}
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#9CA3AF',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                      <span>{t("urgentCase.media.supportedFormats")}</span>
                    </div>
                  </div>
                </div>
                <input
                  id="urgentcase-file"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* Media Previews */}
              {files.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12l2 2 4-4" />
                      <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                      <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                      <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3" />
                      <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3" />
                    </svg>
                    {t("urgentCase.media.previewTitle", { count: files.length })}
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    {files.map((media) => (
                      <div key={media.id} style={{
                        position: 'relative',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '2px solid #E5E7EB',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        transition: 'all 0.2s ease'
                      }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)'
                          e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)'
                          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }}
                      >
                        <button
                          onClick={() => removeFile(media.id)}
                          title={t("urgentCase.media.removeTooltip")}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            zIndex: 10,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#DC2626'
                            e.target.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#EF4444'
                            e.target.style.transform = 'scale(1)'
                          }}
                        >
                          ✕
                        </button>
                        <div style={{
                          aspectRatio: '16/9',
                          backgroundColor: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}>
                          {media.type === 'image' ? (
                            <img
                              src={media.url}
                              alt={media.file.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          ) : (
                            <video
                              src={media.url}
                              controls
                              preload="metadata"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          )}
                        </div>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#FFFFFF'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#1F2937',
                            marginBottom: '4px',
                            fontFamily: '"Red Hat Display", sans-serif',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {media.file.name}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6B7280',
                            fontFamily: '"Red Hat Display", sans-serif'
                          }}>
                            {t("urgentCase.media.fileSize", { size: (media.file.size / 1024 / 1024).toFixed(2) })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{
              marginBottom: '10px',
              padding: '24px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #F3F4F6',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="checkbox"
                    id="urgentcase-ignore-location"
                    checked={ignoreLocation}
                    onChange={() => setIgnoreLocation((v) => !v)}
                    style={{
                      width: '20px',
                      height: '20px',
                      accentColor: '#10B981',
                      cursor: 'pointer'
                    }}
                  />
                </div>
                <label
                  htmlFor="urgentcase-ignore-location"
                  style={{
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    {t("urgentCase.location.ignoreTitle")}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    lineHeight: '1.5',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    {t("urgentCase.location.ignoreDescription")}
                  </div>
                </label>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div style={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span style={{
                  color: '#DC2626',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  {error}
                </span>
              </div>
            )}

            <div style={{
              marginTop: 'auto',
              padding: '24px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #F3F4F6'
            }}>
              <div style={{
                display: 'flex',
                gap: '12px',
                width: '100%'
              }}>
                <button
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    padding: '16px 24px',
                    backgroundColor: '#FFFFFF',
                    color: '#374151',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Red Hat Display", sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#F9FAFB';
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#FFFFFF';
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  {t("urgentCase.location.modal.cancel")}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{
                    flex: 2,
                    padding: '16px 24px',
                    backgroundColor: isSubmitting ? '#9CA3AF' : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    background: isSubmitting ? '#9CA3AF' : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: '"Red Hat Display", sans-serif',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isSubmitting
                      ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                      : '0 8px 20px -5px rgba(239, 68, 68, 0.3), 0 4px 6px -2px rgba(239, 68, 68, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(-2px)'
                      e.target.style.boxShadow = '0 20px 40px -10px rgba(239, 68, 68, 0.4), 0 8px 16px -4px rgba(239, 68, 68, 0.2)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(0)'
                      e.target.style.boxShadow = '0 10px 25px -5px rgba(239, 68, 68, 0.3), 0 4px 6px -2px rgba(239, 68, 68, 0.1)'
                    }
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                        <line x1="12" y1="2" x2="12" y2="6" />
                        <line x1="12" y1="18" x2="12" y2="22" />
                        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                        <line x1="2" y1="12" x2="6" y2="12" />
                        <line x1="18" y1="12" x2="22" y2="12" />
                        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                      </svg>
                      {t("urgentCase.submit.submitting")}
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 2L11 13" />
                        <polygon points="22,2 15,22 11,13 2,9 22,2" />
                      </svg>
                      {t("urgentCase.submit.button")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Map Modal */}
      {showMapModal && (
        <div className="map-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="map-modal" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            height: '600px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <div className="map-modal-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {t("urgentCase.location.modal.title")}
              </h3>
              <button
                onClick={handleCloseMapModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div className="map-modal-content" style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {/* Search Input */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    placeholder={t("urgentCase.location.modal.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#10b981'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                </div>
                <button
                  onClick={handleSearchLocation}
                  disabled={isSearching || !searchQuery.trim()}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: isSearching || !searchQuery.trim() ? '#ccc' : '#10b981',
                    color: 'white',
                    cursor: isSearching || !searchQuery.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: '80px',
                    justifyContent: 'center'
                  }}
                >
                  {isSearching ? '⏳' : '🔍'} {isSearching ? t("urgentCase.location.modal.searching") : t("urgentCase.location.modal.search")}
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  {t("urgentCase.location.modal.instructions")}
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={getCurrentLocation}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #10b981',
                      borderRadius: '6px',
                      background: 'white',
                      color: '#10b981',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    📍 {t("urgentCase.location.modal.getMyLocation")}
                  </button>
                  {selectedLocation && (
                    <button
                      onClick={handleClearPin}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #e53935',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#e53935',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      🗑️ {t("urgentCase.location.modal.clearPin")}
                    </button>
                  )}
                </div>
              </div>

              <div
                className="map-container"
                style={{
                  flex: 1,
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <div
                  id="urgent-case-interactive-map"
                  style={{ width: '100%', height: '100%' }}
                ></div>

                {/* Instruction overlay */}
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  right: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#333',
                  textAlign: 'center',
                  pointerEvents: 'none',
                  zIndex: 15,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {selectedLocation
                    ? t("urgentCase.location.modal.instructionsSelected")
                    : t("urgentCase.location.modal.instructions")}
                </div>
              </div>

              {selectedLocation && (
                <div className="selected-location-info" style={{
                  padding: '12px',
                  backgroundColor: '#f0f8ff',
                  borderRadius: '8px',
                  border: '1px solid #b3d9ff'
                }}>
                  <strong>{t("urgentCase.location.modal.selectedLocation")}</strong><br />
                  {t("urgentCase.location.modal.latitude", { value: selectedLocation.lat.toFixed(6) })}<br />
                  {t("urgentCase.location.modal.longitude", { value: selectedLocation.lng.toFixed(6) })}
                </div>
              )}
            </div>

            <div className="map-modal-footer" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '16px'
            }}>
              <button
                onClick={handleCloseMapModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t("urgentCase.location.modal.cancel")}
              </button>
              <button
                onClick={handleConfirmLocation}
                disabled={!selectedLocation || isSubmitting}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: selectedLocation && !isSubmitting ? '#10b981' : '#ccc',
                  color: 'white',
                  cursor: selectedLocation && !isSubmitting ? 'pointer' : 'not-allowed',
                  fontSize: '14px'
                }}
              >
                {isSubmitting ? t("urgentCase.location.modal.submitting") : t("urgentCase.location.modal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Pets Modal */}
      {showNoPetsModal && (
        <div className="no-pets-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="no-pets-modal" style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#FEF3C7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
              border: '3px solid #F59E0B'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1F2937',
              margin: '0 0 12px 0',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("urgentCase.noPets.title")}
            </h2>

            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("urgentCase.noPets.description")}
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              width: '100%',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => navigate('/medical/dashboard')}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: '"Red Hat Display", sans-serif',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#9CA3AF'
                  e.target.style.backgroundColor = '#F9FAFB'
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#D1D5DB'
                  e.target.style.backgroundColor = 'white'
                }}
              >
                {t("urgentCase.noPets.back")}
              </button>

              <button
                onClick={() => navigate('/pets/add')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: '"Red Hat Display", sans-serif',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3), 0 2px 4px -1px rgba(16, 185, 129, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(16, 185, 129, 0.4), 0 4px 6px -2px rgba(16, 185, 129, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(16, 185, 129, 0.3), 0 2px 4px -1px rgba(16, 185, 129, 0.1)'
                }}
              >
                {t("urgentCase.noPets.addPet")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrgentCaseSubmit;
